import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { Icons } from '../../../model/icon';
import { Header } from '../../../model/header.model';
import { formatCellValue } from '../../../functions/get-value';

export type TableType = 'classic' | 'airports' | 'services' | 'bookings' | 'languages' | 'countries' | 'cities' | 'airport-services' | 'main-screen-images';

@Component({
  selector: 'app-sortable-table',
  standalone: true,
  imports: [NgForOf, NgIf, IconComponent],
  templateUrl: './sortable-table.component.html',
  styleUrls: ['./sortable-table.component.scss']
})
export class SortableTableComponent<T extends { id?: number; image?: string; url?: string; }> implements OnChanges {
  @Input() headers: Header[] = [];
  @Input() data: T[] = [];
  @Input() currentSortHeader: string = '';
  @Input() currentSort: string = '';
  @Input() getClearSorting: number = 0;
  @Input() imageBaseUrl: string = '';
  @Input() activeSection: string = 'classic';
  @Input() tableType: TableType = 'classic';

  @Output() openItemEmit = new EventEmitter<number | string>();
  @Output() currentSortHeaderEmit = new EventEmitter<string>();
  @Output() currentSortEmit = new EventEmitter<string>();
  @Output() sortEmit = new EventEmitter<string>();
  @Output() rowClickEmit = new EventEmitter<any>();

  protected readonly Icons = Icons;

  openItemRow(row: T) {
    const value = this.getRowOpenValue(row);

    this.rowClickEmit.emit(value);

    if (value !== undefined && value !== null && value !== '') {
      this.openItemEmit.emit(value);
    }
  }

  private emitRowValue(row: T) {
    let value: any;

    switch (this.tableType) {
      case 'languages': {
        value = (row as any)['code639_1'];
        break;
      }
      case 'countries': {
        value = (row as any)['code'];
        break;
      }
      case 'cities': {
        value = (row as any)['code'];
        break;
      }
      case 'airport-services': {
        value = (row as any)['airportId'];
        break;
      }
      case 'airports': {
        value = row.id;
        break;
      }
      case 'services': {
        value = row.id;
        break;
      }
      case 'bookings': {
        value = row.id;
        break;
      }
      case 'main-screen-images': {
        value = row.id;
        break;
      }
      default: {
        value = row.id;
        break;
      }
    }

    this.rowClickEmit.emit(value);
  }

  private getRowOpenValue(row: T): number | string | undefined {
    switch (this.tableType) {
      case 'languages':
        return (row as any)['code639_1'];

      case 'countries':
        return (row as any)['code'];

      case 'cities':
        return (
          (row as any)['city_code'] ||
          (row as any)['slug'] ||
          row.id
        );

      case 'airport-services':
        return (row as any)['airportId'];

      case 'airports':
        return (
          (row as any)['iata_code'] ||
          (row as any)['icao_code'] ||
          (row as any)['faa_code'] ||
          (row as any)['city_code'] ||
          (row as any)['slug'] ||
          row.id
        );

      case 'services':
      case 'bookings':
      case 'main-screen-images':
      case 'classic':
      default:
        return row.id;
    }
  }

  openItem(event: Event, row: T) {
    event.stopPropagation();

    const value = this.getRowOpenValue(row);

    if (value !== undefined && value !== null && value !== '') {
      this.openItemEmit.emit(value);
    }
  }

  sort(column: Header) {
    if (column.sortable === false || column.edit) {
      return;
    }

    let newSort = '';
    let newSortHeader = column.key;

    if (this.currentSortHeader === column.key) {
      switch (this.currentSort) {
        case 'asc':
          newSort = 'desc';
          break;
        case 'desc':
          newSort = '';
          newSortHeader = '';
          break;
        default:
          newSort = 'asc';
      }
    } else {
      newSort = 'asc';
    }

    this.currentSortHeader = newSortHeader;
    this.currentSort = newSort;
    this.currentSortHeaderEmit.emit(newSortHeader);
    this.currentSortEmit.emit(newSort);

    const sortQuery = newSort === '' ? '' : `${column.key},${newSort.toUpperCase()}`;
    this.sortEmit.emit(sortQuery);
  }

  clearSorting() {
    this.currentSortHeader = '';
    this.currentSort = '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['getClearSorting'] && this.getClearSorting > 0) {
      this.clearSorting();
    }
  }

  getImageUrl(imagePath?: string): string {
    if (!imagePath) return '';
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    return this.imageBaseUrl ? `${this.imageBaseUrl}${imagePath}` : imagePath;
  }

  getCellValue(row: T, header: Header): any {
    let value = formatCellValue(row, header);

    if (this.tableType === 'classic' || !this.tableType) {
      return value;
    }

    switch (this.tableType) {
      case 'languages': {
        if (header.key === 'code' || header.key === 'code639_1') {
          return (row as any)['code639_1'] || value;
        }
        break;
      }
      case 'countries': {
        if (header.key === 'code') {
          return (row as any)['code'] || value;
        }
        break;
      }
      case 'cities': {
        if (header.key === 'code') {
          return (row as any)['code'] || value;
        }
        break;
      }
      case 'airport-services': {
        if (header.key === 'airportId' || header.key === 'airport_id') {
          return (row as any)['airportId'] || (row as any)['airport_id'] || value;
        }
        break;
      }
      case 'main-screen-images': {
        if (header.key === 'url') {
          return (row as any)['url'] || value;
        }
        break;
      }
      default: {
        break;
      }
    }

    return value;
  }
}
